import React, { createContext, useContext, useEffect, useState } from "react";

import { Renderable, resolveValue, toast, Toast, Toaster, ToastIcon, ValueOrFunction } from "react-hot-toast";

import { CircularProgressIndicator } from "@components/shared/CircularProgressIndicator";
import DialogComponent from "@components/shared/Dialog";
import { DialogRequestProps, DialogReturnProps } from "./types";

const dialogContext = createContext<DialogReturnProps | null>(null);
const { Provider } = dialogContext;
type Props = {
  children: React.ReactNode;
  showToaster?: boolean;
};

export function DialogProvider({ children, showToaster = false }: Props) {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [dialogInfo, setDialogInfo] = useState<DialogRequestProps | null>(null);

  useEffect(() => {
    if (isDialogOpen) {
      const timer = setTimeout(() => {
        document.body.style.pointerEvents = "";
      }, 0);

      return () => clearTimeout(timer);
    } else {
      document.body.style.pointerEvents = "auto";
    }
  }, [isDialogOpen]);

  const closeDialog = async () => {
    if (dialogInfo?.onCancel) dialogInfo?.onCancel();
    setIsDialogOpen(false);
    /// wait for the dialog to close
    await new Promise((resolve) => setTimeout(resolve, 500));
    setDialogInfo(null);
  };

  const refreshDialogContent = (customContent: DialogRequestProps["customContent"]) => {
    if (isDialogOpen && dialogInfo) {
      setDialogInfo({
        ...dialogInfo,
        customContent,
      });
    }
  };

  const openNewDialog = (dialogInfo: DialogRequestProps) => {
    setDialogInfo(dialogInfo);
    setIsDialogOpen(true);
  };

  function openSuccessSnackbar(message: ValueOrFunction<Renderable, Toast>, options?: { duration?: number }) {
    return toast.success(message, { 
      position: "bottom-right",
      duration: options?.duration,
    });
  }

  function openErrorSnackbar(message: string) {
    return toast.error(message, { position: "bottom-right", id: message });
  }

  function openLoadingOverlay(message?: string) {
    setDialogInfo({
      disableBG: true,
      isDismissible: false,
      showActions: false,
      dialogType: "loading",
      customContent: (
        <div className="flex flex-col items-center justify-center gap-2 text-xl font-semibold text-white">
          {message && <p>{message}</p>}
          <CircularProgressIndicator />
        </div>
      ),
    });
    setIsDialogOpen(true);
  }

  function openImagePreview(url: string) {
    setDialogInfo({
      disableBG: true,
      showActions: false,
      customContent: (
        <img
          src={url}
          alt="preview"
          className="w-full cursor-zoom-in rounded-sm bg-lightCard p-2 dark:bg-darkCard"
        ></img>
      ),
    });
    setIsDialogOpen(true);
  }

  return (
    <Provider
      value={{
        openDialog: (e: DialogRequestProps) => openNewDialog(e),
        refreshDialogContent,
        showSuccessSnackbar: openSuccessSnackbar,
        showErrorSnackbar: (e: string) => openErrorSnackbar(e),
        closeDialog,
        openLoadingOverlay,
        openImagePreview,
      }}
    >
      <DialogComponent
        dialogInfo={dialogInfo}
        closeDialog={closeDialog}
        isDialogOpen={isDialogOpen}
      >
        <div className="z-0 h-full w-full">{children}</div>

        {showToaster && <TailwindToaster />}
      </DialogComponent>
    </Provider>
  );
}

function TailwindToaster() {
  return (
    <Toaster position="top-right">
      {(t) => (
        <div className="flex rounded-md bg-popover px-4 py-3 shadow-md">
          <ToastIcon toast={t} />
          <p className="px-2">{resolveValue(t.message, t)}</p>
        </div>
      )}
    </Toaster>
  );
}

export const useDialog: () => DialogReturnProps = () => {
  const context = useContext(dialogContext);
  if (!context) throw "useDialog must be used within a DialogProvider";
  return context;
};

export default useDialog;
